import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {ThemePalette} from '@angular/material/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

/**
 * https://stackblitz.com/edit/ngx-mat-counter-input
 * @title Input with a clear button
 */
@Component({
  selector: 'spt-increment-input',
  templateUrl: './increment-input.component.html',
  styleUrls: ['./increment-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'spt-increment-input'
  }
})
export class IncrementInputComponent implements OnInit, OnDestroy {
  private _onDestroy$: Subject<boolean> = new Subject();

  _step: number = 1;
  _min: number = 0;
  _max: number = Infinity;
  _wrap: boolean = false;
  _color: ThemePalette = undefined;

  incrementFormControl = new UntypedFormControl(0);

  @Output() changes: EventEmitter<number> = new EventEmitter<number>();

  private _label = '';
  @Input()
  set label(v: string | null | undefined) {
    if (v) {
      this._label = v;
      this._cd.markForCheck();
    }
  }
  get label() {
    return this._label;
  }

  private _hintBegin = '';
  @Input()
  set hintBegin(v: string | null | undefined) {
    if (v) {
      this._hintBegin = v;
      this._cd.markForCheck();
    }
  }
  get hintBegin() {
    return this._hintBegin;
  }

  private _hindEnd = '';
  @Input()
  set hintEnd(v: string | null | undefined) {
    if (v) {
      this._hindEnd = v;
      this._cd.markForCheck();
    }
  }
  get hintEnd() {
    return this._hindEnd;
  }

  @Input('value')
  set inputValue(_value: number | undefined) {
    if (_value !== undefined) {
      this.incrementFormControl.setValue(this.parseNumber(_value));
    }
  }

  @Input()
  set step(_step: number) {
    this._step = this.parseNumber(_step);
  }

  @Input()
  set min(_min: number) {
    this._min = this.parseNumber(_min);
  }

  @Input()
  set max(_max: number) {
    this._max = this.parseNumber(_max);
  }

  @Input()
  set wrap(_wrap: boolean) {
    this._wrap = this.parseBoolean(_wrap);
  }

  private parseNumber(num: any): number {
    return +num;
  }

  private parseBoolean(bool: any): boolean {
    return !!bool;
  }

  @Input()
  set color(color: ThemePalette) {
    this._color = color;
  }

  get color(): ThemePalette {
    return this._color;
  }

  constructor(private _cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.incrementFormControl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((inc: number) => {
        this.changes.next(inc);
      });
  }

  ngOnDestroy() {
    this._onDestroy$.next(true);
  }

  incrementValue(step: number = 1): void {
    let inputValue = this.incrementFormControl.value + step;

    if (this._wrap) {
      inputValue = this.wrappedValue(inputValue);
    }

    this.incrementFormControl.setValue(inputValue);
  }

  private wrappedValue(inputValue: number): number {
    if (inputValue > this._max) {
      return this._min + inputValue - this._max;
    }

    if (inputValue < this._min) {
      if (this._max === Infinity) {
        return 0;
      }

      return this._max + inputValue;
    }

    return inputValue;
  }

  shouldDisableDecrement(): boolean {
    return !this._wrap && this.incrementFormControl.value <= this._min;
  }

  shouldDisableIncrement(): boolean {
    return !this._wrap && this.incrementFormControl.value >= this._max;
  }
}
